import { orderBy } from 'lodash'
import * as moment from 'moment'

import { camelCaseObjectKeys } from '@doordash/object-utils'

import {
  DeliveryIssue,
  DeliveryIssueListItem,
  DeliveryIssueStep,
  DeliveryIssueStepResults,
} from '../../../models/types'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'
import { DasherCountries } from './types'
import { DeactivationPolicyUrlsByCountry } from './constants'

/**
 * Formats timestamps like '2020-01-23T18:46:22.913801Z' into 'Jan 23, 2020'
 * Note: Format is localized and can be set with moment.locale if needed.
 * @param timestamp
 */
export const formatTimestamp = (timestamp: string): string =>
  /**
   * Must pass in the format as a string for safety. For example,
   * '2020-01-23T18:46:22.913801Z' <-- Timestamp from backend
   * 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ' <-- Format for Moment
   *
   * Learn more at these links:
   * https://momentjs.com/guides/#/warnings/js-date/
   * https://github.com/moment/moment/issues/1407
   */
  moment(timestamp, 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ').format('ll')

/**
 * An options object defining deeplink URL strings
 */
interface IMakeDeepLinkUrl {
  ios: string
  android: string
}

/**
 * Gives a deeplink URL string depending on the operating system.
 *
 * @param {IMakeDeepLinkUrl} options - an object with "ios" and "android" keys
 * for defining the respective deeplink URL strings.
 * @returns {string} - the resulting deeplink URL string to use
 */
export const makeDeepLinkUrl = (options: IMakeDeepLinkUrl): string =>
  detectWebviewOs() === OsTypes.iOS ? options.ios : options.android

/**
 * Navigates window.location to a deeplink based on the operating system.
 *
 * @param {IMakeDeepLinkUrl} options - an object with "ios" and "android" keys
 * for defining the respective deeplink URL strings.
 */
export const goToDeepLink = (options: IMakeDeepLinkUrl): void => {
  window.location.assign(makeDeepLinkUrl(options))
}

/**
 * Formats the response.data from API GET request for delivery issues for a
 * particular Dasher.
 *
 * @param {*} deliveryIssuesList backend response from /delivery-issues
 * @returns {DeliveryIssue[]}
 */
export const formatDeliveryIssuesList = (
  deliveryIssuesList
): DeliveryIssueListItem[] => {
  const camelCasedList = deliveryIssuesList.map(camelCaseObjectKeys)
  return camelCasedList.map((issue: DeliveryIssue) => {
    const { id, deliveryCreatedAt, priority, status, storeName } = issue
    return {
      deliveryCreatedAt,
      priority,
      status,
      storeName,
      id: `${id}`,
    }
  })
}

/**
 * Formats backend response from getting delivery issue details. Will camelCase
 * the keys, convert number IDs to string IDs.
 *
 * @param {*} { data: deliveryIssueDetails }
 */
export const formatDeliveryIssueDetails = (
  deliveryIssueDetails
): DeliveryIssue =>
  camelCaseObjectKeys({
    ...deliveryIssueDetails,
    id: `${deliveryIssueDetails.id}`,
    deliveryId: `${deliveryIssueDetails.delivery_id}`,
  })

/**
 * Sorts the issues in the delivery issues list from newest to oldest by the
 * deliveryCreatedAt timestamp.
 *
 * @param {DeliveryIssueListItem[]} deliveryIssuesList
 * @returns {DeliveryIssueListItem[]}
 */
export const sortDeliveryIssuesListByNewest = (
  deliveryIssuesList: DeliveryIssueListItem[]
): DeliveryIssueListItem[] =>
  orderBy(
    deliveryIssuesList,
    (issue: DeliveryIssueListItem): number =>
      moment(issue.deliveryCreatedAt).valueOf(),
    'desc'
  )

type countMissedStepsType = (
  steps: DeliveryIssueStep[]
) => { missed: number; total: number }

/**
 * Takes an array of delivery issue steps for a specific delivery issue, then
 * counts how many steps the dasher failed  out of the total number of steps.
 *
 * @param {DeliveryIssueStep[]} [steps=[]]
 * @returns {{ missed: number; total: number }}
 */
export const countMissedSteps: countMissedStepsType = (steps = []) => {
  const totalSteps = steps.length
  let failedSteps = 0
  steps.forEach(({ result }) => {
    if (result === DeliveryIssueStepResults.Failure) {
      failedSteps += 1
    }
  })
  return { missed: failedSteps, total: totalSteps }
}

/**
 * Takes a dasher's country code based on their phone number, then returns the
 * URL to the dasher deactivation policy for that country.
 *
 * @param {DasherCountries} country that comes from dasher phone number
 * @param {DeactivationPolicyUrlsByCountry} urls dictionary of policy URLs
 * @returns {string} the URL to the deactivation policy
 */
export const getDeactivationPolicyUrl = (country: DasherCountries): string =>
  DeactivationPolicyUrlsByCountry[country]
